/**
 * Input validation and sanitization utilities.
 * Used across all forms to prevent injection attacks and ensure data integrity.
 */

/** Strip HTML tags and trim whitespace */
export function sanitize(input: string): string {
  return input.replace(/<[^>]*>/g, "").trim();
}

/** Validate email format */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 255;
}

/** Validate ZIP code (US 5-digit or 5+4). Rejects "00000", which is not an
 *  assigned USPS ZIP and would otherwise pass a plain digit-count check. */
export function isValidZip(zip: string): boolean {
  const trimmed = zip.trim();
  if (!/^\d{5}(-\d{4})?$/.test(trimmed)) return false;
  return trimmed.slice(0, 5) !== "00000";
}

/** Validate an OpenAI API key's shape: "sk-" prefix, plausible length,
 *  key-safe charset. Not a guarantee the key is live — just rejects
 *  obviously-wrong input before it's saved. */
export function isPlausibleOpenAIKey(key: string): boolean {
  const trimmed = key.trim();
  return /^sk-[A-Za-z0-9_-]{20,161}$/.test(trimmed);
}

/** Validate phone number (basic: 10+ digits after stripping formatting).
 *  A leading "-" (e.g. "-1234567890") is rejected even though "-" is
 *  otherwise allowed as a mid-number separator (e.g. "703-555-0192"), since
 *  stripping non-digits alone would let a negative-looking number through. */
export function isValidPhone(phone: string): boolean {
  if (/^\s*-/.test(phone)) return false;
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
}

/** Validate URL format */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return url.length <= 2000;
  } catch {
    return false;
  }
}

/**
 * Prepend https:// to a URL missing a scheme before opening it, so a stored
 * value like "calendly.com/agency" resolves to the external site instead of
 * being treated as a relative path on this app (which 404s in our router).
 */
export function openExternalUrl(url: string) {
  const trimmed = url.trim();
  if (!trimmed) return;
  const href = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  window.open(href, "_blank", "noopener,noreferrer");
}

/** Enforce max length on a string, returning sanitized + truncated */
export function sanitizeField(input: string, maxLength = 500): string {
  return sanitize(input).slice(0, maxLength);
}

/** Validate a name field (non-empty after sanitizing, reasonable length) */
export function isValidName(name: string): boolean {
  const clean = sanitize(name);
  return clean.length >= 1 && clean.length <= 200;
}

/** Validate numeric range */
export function isValidNumber(value: number, min: number, max: number): boolean {
  return !isNaN(value) && value >= min && value <= max;
}

/** Letters (incl. accented), spaces, apostrophes, hyphens, and periods only — for person-name fields */
const PERSON_NAME_PATTERN = /^[a-zA-ZÀ-ſ\s'.-]*$/;

/** True if a person's name contains only letters/spaces/apostrophes/hyphens/periods */
export function isValidPersonName(name: string): boolean {
  return PERSON_NAME_PATTERN.test(name);
}

/** Strip characters a person's name shouldn't contain (digits, symbols) as the user types */
export function filterNameInput(value: string): string {
  return value.replace(/[^a-zA-ZÀ-ſ\s'.-]/g, "");
}

/** True if a string contains at least one letter or digit — rejects symbols-only junk
 *  (e.g. "@#$&*") in free-text fields that allow punctuation but must have real content. */
export function hasAlphanumeric(value: string): boolean {
  return /[a-zA-Z0-9À-ſ]/.test(value);
}

/** Strip characters a phone number shouldn't contain as the user types */
export function filterPhoneInput(value: string): string {
  return value.replace(/[^0-9+()\-\s]/g, "");
}

/** Strip to digits only as the user types (for numeric text inputs) */
export function filterDigitsInput(value: string): string {
  return value.replace(/\D/g, "");
}

/** Strip characters a US ZIP code shouldn't contain (keeps digits and the ZIP+4 dash) */
export function filterZipInput(value: string): string {
  return value.replace(/[^0-9-]/g, "");
}

/** Strip to letters only, upper-cased (for 2-letter state codes) */
export function filterStateInput(value: string): string {
  return value.replace(/[^a-zA-Z]/g, "").toUpperCase();
}

/**
 * Parse a "yyyy-MM-dd" string as a local calendar date. `new Date("2026-07-13")`
 * parses date-only ISO strings as UTC midnight, which renders as the previous
 * day in any timezone behind UTC — this avoids that footgun for date-picker values.
 */
export function parseLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Format a Date as a local "yyyy-MM-dd" string (no UTC conversion). */
export function toLocalYMD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** USPS abbreviations for the 50 states + DC and the inhabited US territories. */
const US_STATE_ABBREVIATIONS = new Set([
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC", "PR", "VI", "GU", "AS", "MP",
]);

/** True if a 2-letter code is a real USPS state/territory abbreviation */
export function isValidStateAbbr(value: string): boolean {
  return US_STATE_ABBREVIATIONS.has(value.trim().toUpperCase());
}

/**
 * supabase.functions.invoke() throws a FunctionsHttpError whose .message is
 * always the generic "Edge Function returned a non-2xx status code" — the
 * actual JSON body (e.g. { error: "Rate limit exceeded", retry_after_seconds })
 * is on error.context, a raw Response that has to be read separately. This
 * unwraps it so callers can show the real message (and retry time) instead.
 */
export async function getEdgeFunctionErrorMessage(error: any, fallback: string): Promise<string> {
  const response: Response | undefined = error?.context;
  if (response && typeof response.json === "function") {
    try {
      const body = await response.clone().json();
      if (response.status === 429) {
        const retryAfter = Number(body?.retry_after_seconds);
        if (Number.isFinite(retryAfter) && retryAfter > 0) {
          return `You're searching too quickly. Please wait ${retryAfter}s and try again.`;
        }
        return "You're searching too quickly. Please wait a moment and try again.";
      }
      if (typeof body?.error === "string") return body.error;
    } catch {
      // Response body wasn't JSON — fall through to the generic message.
    }
  }
  return error?.message || fallback;
}
